import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/uptk/core/lib/utils';
import DensityContext from '@/uptk/core/packages/theme/DensityContext';

// Define button variants using CVA (Class Variance Authority)
// Colors match exact MUI Button palette:
// Light: regular (grey60 bg, grey900 text), cta (blue500 bg, white text), secondary (transparent bg, grey900 text)
// Dark: regular (grey300 bg, grey20 text), cta (blue500 bg, white text), secondary (transparent bg, grey20 text)
// UPT-1802 (standard mobile-first §7): `pointer-coarse:min-h-11` guarantees a 44px
// touch target on coarse pointers only — desktop (fine pointer) heights are untouched.
const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 pointer-coarse:min-h-11',
  {
    variants: {
      variant: {
        // Modern variants with dark mode support - using !text to force override MUI styles
        primary:
          'bg-theme-primary !text-theme-primary-foreground hover:bg-theme-primary/90 active:bg-theme-primary focus-visible:ring-theme-ring',
        secondary:
          'bg-transparent !text-theme-foreground hover:bg-theme-muted active:bg-theme-muted focus-visible:ring-theme-ring',
        destructive:
          'bg-theme-destructive !text-theme-destructive-foreground hover:bg-theme-destructive/85 focus-visible:ring-theme-destructive',
        outline:
          'border border-theme-border bg-transparent !text-theme-foreground hover:bg-theme-muted focus-visible:ring-theme-ring',
        themeOutline:
          'border border-portal-border bg-portal-surface !text-portal-ink hover:border-portal-accent/45 hover:bg-portal-surface-muted active:bg-portal-surface-muted focus-visible:ring-portal-accent',
        workspacePrimary:
          'border border-transparent bg-theme-primary !text-theme-primary-foreground hover:bg-theme-primary/90 active:bg-theme-primary focus-visible:ring-theme-ring',
        workspaceOutlined:
          'border border-theme-primary bg-bg-panel !text-theme-primary hover:bg-bg-surface-muted active:bg-bg-surface focus-visible:ring-theme-ring',
        ghost:
          '!text-theme-foreground hover:bg-theme-muted focus-visible:ring-theme-ring',
        link: '!text-theme-primary underline-offset-4 hover:underline',
        nav: 'bg-transparent text-portal-ink-muted hover:bg-portal-surface-muted hover:text-portal-ink focus-visible:ring-portal-border',
        // MUI compatibility variants - EXACT color match - using !important for color
        cta:
          'border border-theme-primary/65 bg-theme-primary !text-theme-primary-foreground hover:bg-theme-primary/90 active:bg-theme-primary focus-visible:ring-theme-ring',
        regular:
          'bg-theme-muted !text-theme-foreground hover:bg-theme-muted/80 active:bg-theme-muted/70 focus-visible:ring-theme-ring',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 py-2 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
      },
      density: {
        touch: 'h-11 px-4 text-sm',
        low: 'h-8 px-4 text-xs min-h-0',
        medium: 'h-8 px-4 text-xs min-h-0',
        high: 'h-5 px-2 text-[10px] min-h-0 leading-none',
      },
    },
    defaultVariants: {
      variant: 'regular',
      size: 'md',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  fullWidth?: boolean;
  icon?: string; // Legacy MUI prop for icon class (e.g., 'fa fa-check')
}

/**
 * Modern Button component to replace Material-UI Button
 *
 * Supports both modern variants (primary, secondary, etc.) and legacy MUI variants (cta, regular, secondary)
 * Supports density levels for compact layouts (touch, low, medium, high)
 *
 * Usage:
 * ```tsx
 * <Button variant="cta" density="medium" onClick={handleClick}>
 *   Click me
 * </Button>
 *
 * // Legacy MUI-style with icon prop
 * <Button icon="fa fa-check">Save</Button>
 * ```
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, density, fullWidth, icon, children, ...props }, ref) => {
    // Mobile-first Lot 0 (mobile-first-shell-proposal.md §2d, arbitrage 6):
    // under the md breakpoint the shell-level ResponsiveDensityProvider
    // publishes the `touch` density (44px targets, UPT-1286). Only the
    // `touch` value is inherited from context — the desktop context value
    // (`medium`) is ignored so desktop keeps the size-based rendering
    // (no desktop visual change). An explicit `density` prop always wins.
    const contextDensity = React.useContext(DensityContext);
    const effectiveDensity =
      density ?? (contextDensity === 'touch' ? 'touch' : undefined);
    return (
      <button
        className={cn(
          buttonVariants({
            variant,
            size: effectiveDensity ? undefined : size,
            density: effectiveDensity,
          }),
          // Use !important utilities to win specificity battles against
          // .portal-theme button selectors WITHOUT blocking hover states
          // (inline styles override :hover, but !important Tailwind classes do not)
          //
          // NOTE: design-system/components.css styles variants via CASE-SENSITIVE
          // attribute selectors like `.portal-theme button[class*="primary"]`.
          // Those do NOT match the camelCase `workspacePrimary` / `workspaceOutlined`
          // class names, so without the explicit !important overrides below the
          // workspace buttons fall back to `.portal-theme button { color: inherit }`
          // and render unreadable text in light mode. Keep an override entry for
          // every variant whose foreground/background must beat portal-theme.
          variant === 'primary' && '!bg-theme-primary !text-theme-primary-foreground hover:!bg-theme-primary/90 active:!bg-theme-primary border-0',
          variant === 'workspacePrimary' && '!bg-theme-primary !text-theme-primary-foreground hover:!bg-theme-primary/90 active:!bg-theme-primary',
          variant === 'workspaceOutlined' && '!bg-transparent !text-theme-primary border !border-theme-primary hover:!bg-theme-muted',
          variant === 'cta' && '!bg-theme-primary !text-theme-primary-foreground hover:!bg-theme-primary/90 active:!bg-theme-primary border-0',
          variant === 'destructive' && '!bg-theme-destructive !text-theme-destructive-foreground hover:!bg-theme-destructive/85 border-0',
          variant === 'secondary' && '!bg-transparent border-0',
          variant === 'outline' && '!bg-transparent',
          variant === 'ghost' && '!bg-transparent border-0',
          fullWidth && 'w-full',
          className
        )}
        ref={ref}
        {...props}
      >
        {icon && <i className={cn(icon, children && 'mr-2')} aria-hidden="true" />}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
export default Button;
